import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { TrendingUp, Percent, Clock, Trophy, Layers, IndianRupee } from "lucide-react";

const PIE_COLORS = ["#003DA5", "#0055D4", "#0071E3", "#3A8DFF", "#7BB0FF",
                    "#FF7A00", "#FFB01A", "#00A86B", "#10B981", "#A855F7"];

export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [dist, setDist] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [cycle, setCycle] = useState([]);
  const [garages, setGarages] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/admin/analytics/overview").then((r) => setOverview(r.data)),
      api.get("/admin/analytics/status-distribution").then((r) => setDist(r.data)),
      api.get("/admin/analytics/monthly-trend").then((r) => setMonthly(r.data)),
      api.get("/admin/analytics/cycle-time-trend").then((r) => setCycle(r.data)),
      api.get("/admin/analytics/top-garages").then((r) => setGarages(r.data)),
      api.get("/admin/analytics/insurer-mix").then((r) => setInsurers(r.data)),
    ])
      .catch((e) => setError(e.response?.data?.detail || "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center text-slate-500">Loading analytics…</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)] mesh-subtle py-10" data-testid="admin-analytics-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#003DA5] dark:text-blue-400 mb-2 flex items-center gap-2">
            <TrendingUp className="w-3 h-3" /> Admin Analytics
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Ops KPIs at a glance.
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Investor-grade metrics: settlement ratios, cycle time, network performance, insurer mix.</p>
        </div>

        {error && <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 p-4 text-sm text-rose-700 dark:text-rose-300">{error}</div>}

        {/* Overview cards */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <KpiCard testid="an-total" icon={Layers} label="Total claims" value={overview.total_claims.toLocaleString("en-IN")} tone="from-[#003DA5] to-blue-900" />
            <KpiCard testid="an-settled" icon={IndianRupee} label="Settled" value={overview.settled.toLocaleString("en-IN")} tone="from-[#00A86B] to-emerald-700" />
            <KpiCard testid="an-ratio" icon={Percent} label="Settlement ratio" value={`${overview.settlement_ratio_pct}%`} tone="from-[#FF7A00] to-orange-700" />
            <KpiCard testid="an-cycle" icon={Clock} label="Avg cycle" value={`${overview.avg_cycle_days} days`} tone="from-fuchsia-500 to-fuchsia-700" />
          </div>
        )}

        {/* Monthly trend */}
        <ChartCard title="Monthly filed vs settled" subtitle="Rolling 12 months" testid="an-monthly">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
              <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0B1220", border: "none", borderRadius: 12, color: "#fff" }} />
              <Legend />
              <Line type="monotone" dataKey="filed" name="Filed" stroke="#003DA5" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="settled" name="Settled" stroke="#00A86B" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Cycle time */}
          <ChartCard title="Average cycle time" subtitle="Days from filed → settled" testid="an-cycle-chart">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={cycle}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} unit="d" />
                <Tooltip contentStyle={{ background: "#0B1220", border: "none", borderRadius: 12, color: "#fff" }} />
                <Line type="monotone" dataKey="avg_days" name="Avg days" stroke="#FF7A00" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Status pie */}
          <ChartCard title="Status distribution" subtitle="Live claim funnel" testid="an-status">
            {dist.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={dist} dataKey="count" nameKey="status" outerRadius={90} innerRadius={45} paddingAngle={2}>
                    {dist.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0B1220", border: "none", borderRadius: 12, color: "#fff" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Top garages */}
          <ChartCard title="Top garages" subtitle="By completed repairs & revenue" testid="an-garages">
            {garages.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={garages.slice(0, 8)} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                  <XAxis type="number" stroke="#94A3B8" fontSize={11} />
                  <YAxis dataKey="workshop" type="category" width={130} stroke="#94A3B8" fontSize={10} interval={0} />
                  <Tooltip contentStyle={{ background: "#0B1220", border: "none", borderRadius: 12, color: "#fff" }} />
                  <Bar dataKey="completed" name="Completed" fill="#003DA5" radius={[0, 6, 6, 0]} />
                  <Bar dataKey="count" name="Total QRs" fill="#FF7A00" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Insurer mix */}
          <ChartCard title="Insurer mix" subtitle="Top insurers by claim volume" testid="an-insurers">
            {insurers.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={insurers.slice(0, 8)} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                  <XAxis type="number" stroke="#94A3B8" fontSize={11} />
                  <YAxis dataKey="insurer" type="category" width={130} stroke="#94A3B8" fontSize={10} interval={0} />
                  <Tooltip contentStyle={{ background: "#0B1220", border: "none", borderRadius: 12, color: "#fff" }} />
                  <Bar dataKey="count" name="Claims" fill="#00A86B" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className="card-premium p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tone} flex items-center justify-center mb-3 shadow-md`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
      <div className="font-heading font-extrabold text-3xl text-slate-900 dark:text-white mt-0.5">{value}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, testid, children }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5" data-testid={testid}>
      <div className="mb-3">
        <div className="font-heading font-bold text-slate-900 dark:text-white">{title}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</div>
      </div>
      {children}
    </div>
  );
}

function EmptyChart() {
  return <div className="h-[220px] flex items-center justify-center text-sm text-slate-500">No data yet — file a few claims first.</div>;
}
